'use client';

/**
 * Service Referral Workspace Component
 *
 * Unified workspace for managing a service referral, integrating:
 * - Referral details and status
 * - Service delivery progress
 * - Goals tracking
 * - Milestones dashboard
 * - Billing integration
 *
 * This component enhances the existing case workspace by providing
 * a comprehensive view of service referral management.
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  Activity,
  Target,
  Award,
  CreditCard,
  Clock,
  User,
  Building2,
  Calendar,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  Loader2,
  RefreshCw,
  ExternalLink,
  Heart,
  DollarSign,
  Users,
  MessageSquare,
  UserCheck,
  Bot,
  Workflow,
} from 'lucide-react';
import {
  serviceReferralsAPI,
  ServiceReferral,
  getReferralStatusLabel,
  getReferralStatusColor,
  getUrgencyLabel,
  getUrgencyColor,
} from '@/services/api/serviceReferrals';
import { tcoServiceReferralsAPI, TCOServiceReferral } from '@/services/api/tcoServiceReferrals';
import { ServiceDeliveryProgress } from './ServiceDeliveryProgress';
import { ServiceGoalsTracker } from './ServiceGoalsTracker';
import { MilestonesDashboard } from './MilestonesDashboard';
import { BillingIntegration } from './BillingIntegration';
import { ConsentContextPanel } from './ConsentContextPanel';
import { SDOHAssessmentCard } from './SDOHAssessmentCard';
import { SDOHAssessmentForm } from './SDOHAssessmentForm';
import { SDOHContextPanel } from './SDOHContextPanel';
import { PricingProposalDetail } from './PricingProposalDetail';
import { PricingProposalForm } from './PricingProposalForm';
import { PricingContextPanel } from './PricingContextPanel';
import { ServiceReferralJourney } from './ServiceReferralJourney';
import { ProviderMatchingPanel } from './ProviderMatchingPanel';
import { CaseNotesPanel } from './CaseNotesPanel';
import { ProviderAcceptancePanel } from './ProviderAcceptancePanel';
import { PricingAgentPanel } from './PricingAgentPanel';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ServiceReferralWorkspaceProps {
  referralId: number;
  /** @deprecated Use onBack instead */
  onClose?: () => void;
  /** Callback when back button is clicked */
  onBack?: () => void;
  onStatusChange?: (referral: ServiceReferral) => void;
  readOnly?: boolean;
  initialTab?: 'overview' | 'matching' | 'acceptance' | 'progress' | 'goals' | 'milestones' | 'billing' | 'sdoh' | 'pricing' | 'notes' | 'agent';
  userRole?: 'admin' | 'provider' | 'member' | 'program_manager' | 'employer';
  className?: string;
  /** Use TCO service referrals API instead of standard service referrals API */
  useTcoApi?: boolean;
  /** Callback to view linked workflow instance */
  onViewWorkflow?: (workflowInstanceId: number) => void;
  /** Optional linked workflow instance ID for display */
  linkedWorkflowInstanceId?: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ServiceReferralWorkspace({
  referralId,
  onClose,
  onBack,
  onStatusChange,
  readOnly = false,
  initialTab = 'overview',
  userRole = 'admin',
  className = '',
  useTcoApi = false,
  onViewWorkflow,
  linkedWorkflowInstanceId,
}: ServiceReferralWorkspaceProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // Use onBack if provided, fall back to onClose for backwards compatibility
  const handleBack = onBack || onClose;
  // State
  const [referral, setReferral] = useState<ServiceReferral | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showSDOHForm, setShowSDOHForm] = useState(false);
  const [showPricingForm, setShowPricingForm] = useState(false);
  // Track selected/accepted match for pricing when selected_provider_id is not set on referral
  const [selectedMatch, setSelectedMatch] = useState<{
    provider_type: string;
    provider_id: string | number;
    provider_name: string;
  } | null>(null);

  // Load referral data
  useEffect(() => {
    loadReferral();
  }, [referralId, useTcoApi]);

  const loadReferral = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use TCO API or standard service referrals API based on prop
      let data: ServiceReferral;
      if (useTcoApi) {
        const tcoData = await tcoServiceReferralsAPI.get(referralId);
        // Map TCO referral to ServiceReferral type (they have similar structure)
        data = {
          ...tcoData,
          employee_id: tcoData.member_id,
          employer_id: tcoData.cbo_id,
          urgency: tcoData.urgency_level as any,
          assigned_provider_id: tcoData.selected_provider_id,
          assigned_provider_name: tcoData.selected_provider_name,
        } as ServiceReferral;
      } else {
        data = await serviceReferralsAPI.getReferral(referralId);
      }
      setReferral(data);

      // If no selected_provider_id on referral, check for accepted match
      if (!data.selected_provider_id) {
        try {
          const matches = useTcoApi
            ? await tcoServiceReferralsAPI.getMatches(referralId)
            : await serviceReferralsAPI.getMatches(referralId);
          const acceptedMatch = matches.find((m: any) => m.is_selected) ||
            matches.find((m: any) => m.response_status === 'accepted');
          if (acceptedMatch) {
            setSelectedMatch({
              provider_type: acceptedMatch.provider_type,
              provider_id: acceptedMatch.provider_id,
              provider_name: acceptedMatch.provider_name || '',
            });
          }
        } catch (matchErr) {
          console.error('Failed to load matches:', matchErr);
        }
      }
    } catch (err) {
      console.error('Failed to load referral:', err);
      setError('Failed to load referral details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return fmt.medium(dateStr);
  };

  const handleStatusTransition = async (newStatus: string, notes?: string) => {
    if (!referral) return;
    try {
      const result = await serviceReferralsAPI.transitionReferralStatus(
        referral.id,
        newStatus as any,
        notes
      );
      if (result.success) {
        // Reload the referral to get updated data
        await loadReferral();
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      setError('Failed to update referral status');
    }
  };

  // Calculate progress percentage
  const progressPct = referral?.estimated_units && referral.estimated_units > 0
    ? Math.min(100, ((referral.actual_units || 0) / referral.estimated_units) * 100)
    : 0;

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  const renderStatusBadge = () => {
    if (!referral) return null;
    const color = getReferralStatusColor(referral.status);
    const label = getReferralStatusLabel(referral.status);

    return (
      <Badge
        variant="outline"
        className={`
          ${color === 'gray' ? 'border-gray-300 text-gray-700 bg-gray-50' : ''}
          ${color === 'yellow' ? 'border-yellow-300 text-yellow-700 bg-yellow-50' : ''}
          ${color === 'blue' ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}
          ${color === 'green' ? 'border-green-300 text-green-700 bg-green-50' : ''}
          ${color === 'red' ? 'border-red-300 text-red-700 bg-red-50' : ''}
          ${color === 'purple' ? 'border-purple-300 text-purple-700 bg-purple-50' : ''}
        `}
      >
        {label}
      </Badge>
    );
  };

  const renderUrgencyBadge = () => {
    if (!referral) return null;
    const color = getUrgencyColor(referral.urgency);
    const label = getUrgencyLabel(referral.urgency);

    return (
      <Badge
        variant="outline"
        className={`
          ${color === 'gray' ? 'border-gray-300 text-gray-700' : ''}
          ${color === 'orange' ? 'border-orange-300 text-orange-700 bg-orange-50' : ''}
          ${color === 'red' ? 'border-red-300 text-red-700 bg-red-50' : ''}
        `}
      >
        {label}
      </Badge>
    );
  };

  const renderOverviewTab = () => {
    if (!referral) return null;

    return (
      <div className="space-y-6">
        {/* Journey Timeline - shows workflow progress */}
        <ServiceReferralJourney referral={referral} />

        {/* Consent Context Panel - shows if referral was created from consent */}
        <ConsentContextPanel referralId={referralId} collapsed={true} />

        {/* Linked Workflow Panel - shows if referral was created from a workflow */}
        {linkedWorkflowInstanceId && (
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Workflow className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Linked Workflow</p>
                    <p className="text-sm text-gray-600">
                      This referral was created from Workflow #{linkedWorkflowInstanceId}
                    </p>
                  </div>
                </div>
                {onViewWorkflow && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewWorkflow(linkedWorkflowInstanceId)}
                  >
                    View Workflow
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Key Information */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Referral Number</p>
                  <p className="font-medium font-mono">{referral.referral_number}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded">
                  <Activity className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className="mt-1">{renderStatusBadge()}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Urgency</p>
                  <div className="mt-1">{renderUrgencyBadge()}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Service Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Service Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Service Type</p>
                <p className="font-medium">{referral.service_type_name || referral.service_type_code}</p>
              </div>
              <div>
                <p className="text-gray-500">Provider</p>
                <p className="font-medium">{referral.assigned_provider_name || 'Not assigned'}</p>
              </div>
              <div>
                <p className="text-gray-500">Start Date</p>
                <p className="font-medium">{formatDate(referral.actual_start_date || referral.requested_start_date)}</p>
              </div>
              <div>
                <p className="text-gray-500">End Date</p>
                <p className="font-medium">{formatDate(referral.estimated_end_date)}</p>
              </div>
              <div>
                <p className="text-gray-500">Units Progress</p>
                <p className="font-medium">
                  {referral.actual_units || 0} / {referral.estimated_units || '-'} ({Math.round(progressPct)}%)
                </p>
              </div>
              <div>
                <p className="text-gray-500">Consent</p>
                <p className="font-medium">
                  {referral.consent_obtained ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Obtained {formatDate(referral.consent_date)}
                    </span>
                  ) : (
                    <span className="text-amber-600">Pending</span>
                  )}
                </p>
              </div>
            </div>

            {referral.referral_reason && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">Referral Reason</p>
                <p className="mt-1">{referral.referral_reason}</p>
              </div>
            )}

            {referral.clinical_notes && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">Clinical Notes</p>
                <p className="mt-1">{referral.clinical_notes}</p>
              </div>
            )}

            {referral.care_plan_summary && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">Care Plan Summary</p>
                <p className="mt-1">{referral.care_plan_summary}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Outcome (if completed) */}
        {referral.outcome_status && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Service Outcome</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Outcome Status</p>
                  <Badge
                    variant="outline"
                    className={
                      referral.outcome_status === 'successful'
                        ? 'border-green-300 text-green-700 bg-green-50'
                        : 'border-amber-300 text-amber-700 bg-amber-50'
                    }
                  >
                    {referral.outcome_status}
                  </Badge>
                </div>
                {referral.satisfaction_score && (
                  <div>
                    <p className="text-gray-500">Satisfaction Score</p>
                    <p className="font-medium">{referral.satisfaction_score}/5</p>
                  </div>
                )}
              </div>
              {referral.outcome_notes && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500">Outcome Notes</p>
                  <p className="mt-1">{referral.outcome_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        {!readOnly && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {referral.status === 'provider_notified' && (
                  <>
                    <Button
                      variant="default"
                      onClick={() => handleStatusTransition('provider_accepted')}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Accept Referral
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleStatusTransition('provider_declined', 'Unable to accept at this time')}
                    >
                      Decline
                    </Button>
                  </>
                )}
                {referral.status === 'provider_accepted' && (
                  <Button
                    variant="default"
                    onClick={() => handleStatusTransition('service_scheduled')}
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Schedule Service
                  </Button>
                )}
                {referral.status === 'service_scheduled' && (
                  <Button
                    variant="default"
                    onClick={() => handleStatusTransition('service_started')}
                  >
                    <Activity className="w-4 h-4 mr-1" />
                    Start Service
                  </Button>
                )}
                {referral.status === 'service_started' && (
                  <Button
                    variant="default"
                    onClick={() => handleStatusTransition('service_completed')}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Complete Service
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Compact Sidebar Cards */}
        <div className="grid grid-cols-2 gap-4">
          <MilestonesDashboard referralId={referralId} compact readOnly={readOnly} />
          <BillingIntegration referralId={referralId} compact readOnly={readOnly} />
        </div>
      </div>
    );
  };

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-12 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !referral) {
    return (
      <div className={`p-6 ${className}`}>
        <Card>
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <p className="text-gray-600">{error || 'Referral not found'}</p>
            <Button variant="outline" className="mt-4" onClick={loadReferral}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {handleBack && (
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              Service Referral
              <span className="font-mono text-gray-500">{referral.referral_number}</span>
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {renderStatusBadge()}
              {renderUrgencyBadge()}
              <span className="text-sm text-gray-500">
                {referral.service_type_name || referral.service_type_code}
              </span>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadReferral}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="sdoh" className="flex items-center gap-1">
            <Heart className="w-4 h-4" />
            SDOH
          </TabsTrigger>
          <TabsTrigger value="matching" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Matching
          </TabsTrigger>
          <TabsTrigger value="acceptance" className="flex items-center gap-1">
            <UserCheck className="w-4 h-4" />
            Acceptance
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center gap-1">
            <DollarSign className="w-4 h-4" />
            Pricing
          </TabsTrigger>
          <TabsTrigger value="agent" className="flex items-center gap-1">
            <Bot className="w-4 h-4" />
            AI Agent
          </TabsTrigger>
          <TabsTrigger value="progress" className="flex items-center gap-1">
            <Activity className="w-4 h-4" />
            Progress
          </TabsTrigger>
          <TabsTrigger value="goals" className="flex items-center gap-1">
            <Target className="w-4 h-4" />
            Goals
          </TabsTrigger>
          <TabsTrigger value="milestones" className="flex items-center gap-1">
            <Award className="w-4 h-4" />
            Milestones
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-1">
            <MessageSquare className="w-4 h-4" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-1">
            <CreditCard className="w-4 h-4" />
            Billing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {renderOverviewTab()}
        </TabsContent>

        <TabsContent value="sdoh">
          <div className="space-y-6">
            {showSDOHForm ? (
              <SDOHAssessmentForm
                referralId={referralId}
                existingAssessment={referral?.sdoh_assessment || undefined}
                existingNeeds={referral?.sdoh_needs || []}
                onSave={async (assessment, needs) => {
                  try {
                    // Call API to save SDOH assessment
                    await serviceReferralsAPI.saveSDOHAssessment(referralId, {
                      assessment_type: assessment.assessment_type,
                      assessment_tool: assessment.assessment_tool,
                      housing_score: assessment.housing_score,
                      food_score: assessment.food_score,
                      transportation_score: assessment.transportation_score,
                      economic_score: assessment.economic_score,
                      education_employment_score: assessment.education_employment_score,
                      social_support_score: assessment.social_support_score,
                      health_behaviors_score: assessment.health_behaviors_score,
                      safety_score: assessment.safety_score,
                      requires_follow_up: assessment.requires_follow_up,
                      follow_up_date: assessment.follow_up_date,
                      notes: assessment.notes,
                      needs: needs.map(n => ({
                        domain_code: n.domain_code as any,
                        description: n.description,
                        severity: n.severity as any,
                        is_primary: n.is_primary,
                      })),
                    });
                    setShowSDOHForm(false);
                    await loadReferral();
                  } catch (err) {
                    console.error('Failed to save SDOH assessment:', err);
                    setError('Failed to save SDOH assessment');
                  }
                }}
                onCancel={() => setShowSDOHForm(false)}
                readOnly={readOnly}
              />
            ) : (
              <>
                {/* Action buttons for manual assessment */}
                {!readOnly && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setShowSDOHForm(true)}
                    >
                      {referral?.sdoh_assessment ? (
                        <>
                          <FileText className="h-4 w-4 mr-2" />
                          Edit Manual Assessment
                        </>
                      ) : (
                        <>
                          <Heart className="h-4 w-4 mr-2" />
                          Complete Manual Assessment
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* SDOH Context Panel - Shows ML predictions, area data, workflow context */}
                <SDOHContextPanel referralId={referralId} />
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="progress">
          <ServiceDeliveryProgress
            referralId={referralId}
            estimatedUnits={referral.estimated_units}
            actualUnits={referral.actual_units}
            readOnly={readOnly}
            onProgressAdded={() => loadReferral()}
          />
        </TabsContent>

        <TabsContent value="goals">
          <ServiceGoalsTracker
            referralId={referralId}
            readOnly={readOnly}
            onGoalUpdated={() => loadReferral()}
          />
        </TabsContent>

        <TabsContent value="milestones">
          <MilestonesDashboard
            referralId={referralId}
            readOnly={readOnly}
            onMilestoneAchieved={() => loadReferral()}
          />
        </TabsContent>

        <TabsContent value="pricing">
          <div className="space-y-6">
            {/* Always show pricing context with ML comparatives */}
            {referral?.service_type_code && (
              <PricingContextPanel
                serviceTypeCode={referral.service_type_code}
                providerType={referral?.selected_provider_type || selectedMatch?.provider_type || 'cbo'}
                providerId={referral?.selected_provider_id || selectedMatch?.provider_id}
                urgencyLevel={referral?.urgency as 'routine' | 'urgent' | 'emergent'}
                acceptedRate={referral?.estimated_rate}
                providerName={referral?.selected_provider_name || selectedMatch?.provider_name}
                showComparatives={true}
                showMLRecommendation={true}
              />
            )}

            {/* Show proposal detail if exists */}
            {referral?.active_proposal_id ? (
              <PricingProposalDetail
                proposalId={referral.active_proposal_id}
                canRespond={!readOnly}
                canManageMilestones={!readOnly}
                showActions={!readOnly}
                onAccept={() => loadReferral()}
                onReject={() => loadReferral()}
                onCounter={() => {}}
              />
            ) : referral?.accepted_proposal_id ? (
              <PricingProposalDetail
                proposalId={referral.accepted_proposal_id}
                canRespond={false}
                canManageMilestones={!readOnly}
                showActions={false}
              />
            ) : (referral?.status === 'provider_accepted' || referral?.status === 'service_started') && (referral?.selected_provider_id || selectedMatch) ? (
              /* Provider has accepted - show accepted pricing summary */
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Pricing Accepted
                  </CardTitle>
                  <CardDescription>
                    Provider has accepted the referral with the agreed pricing
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                      <div>
                        <p className="font-medium text-green-800">Accepted Rate</p>
                        <p className="text-sm text-green-600">
                          {referral?.selected_provider_name || selectedMatch?.provider_name || 'Provider'}
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-green-700">
                        ${referral?.estimated_rate?.toFixed(2) || '150.00'}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Service Type</p>
                        <p className="font-medium">{referral?.service_type_code?.replace(/_/g, ' ')}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Urgency</p>
                        <p className="font-medium capitalize">{referral?.urgency || 'Routine'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Status</p>
                        <Badge variant="default" className="bg-green-100 text-green-800">
                          {referral?.status?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-gray-500">Provider Type</p>
                        <p className="font-medium uppercase">{referral?.selected_provider_type || selectedMatch?.provider_type || 'CBO'}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : !readOnly && (referral?.selected_provider_id || selectedMatch) ? (
              /* Provider matched but not yet accepted - show proposal form */
              <PricingProposalForm
                referralId={referralId}
                providerType={referral?.selected_provider_type || selectedMatch?.provider_type || 'cbo'}
                providerId={referral?.selected_provider_id || selectedMatch?.provider_id}
                providerName={referral?.selected_provider_name || selectedMatch?.provider_name}
                serviceTypeCode={referral?.service_type_code}
                urgencyLevel={referral?.urgency}
                onSubmit={() => loadReferral()}
                onCancel={() => setActiveTab('overview')}
              />
            ) : (
              <Card>
                <CardContent className="p-6 text-center text-gray-500">
                  <DollarSign className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No pricing proposal available</p>
                  <p className="text-sm mt-1">
                    {referral?.selected_provider_id || selectedMatch
                      ? 'Create a pricing proposal to start the negotiation process'
                      : 'A provider must be selected before creating a pricing proposal'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="agent">
          <PricingAgentPanel
            referralId={referralId}
            referral={referral!}
            providerId={referral?.selected_provider_id || selectedMatch?.provider_id}
            providerType={referral?.selected_provider_type || selectedMatch?.provider_type}
            providerName={referral?.selected_provider_name || selectedMatch?.provider_name}
            readOnly={readOnly}
            onNegotiationCompleted={() => loadReferral()}
          />
        </TabsContent>

        <TabsContent value="matching">
          <ProviderMatchingPanel
            referralId={referralId}
            referral={referral!}
            readOnly={readOnly}
            onProviderSelected={() => loadReferral()}
            onMatchingTriggered={() => loadReferral()}
          />
        </TabsContent>

        <TabsContent value="acceptance">
          <ProviderAcceptancePanel
            referralId={referralId}
            referral={referral!}
            isProviderView={userRole === 'provider'}
            readOnly={readOnly}
            onAcceptanceUpdated={() => loadReferral()}
          />
        </TabsContent>

        <TabsContent value="notes">
          <CaseNotesPanel
            referralId={referralId}
            currentUserRole={userRole}
            readOnly={readOnly}
            showPrivateNotes={userRole !== 'employer'}
          />
        </TabsContent>

        <TabsContent value="billing">
          <BillingIntegration
            referralId={referralId}
            readOnly={readOnly}
            onBillingCreated={() => loadReferral()}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ServiceReferralWorkspace;
